import { dungApiThat, goiApi } from '../api/http';
import { mayChuDiaChiRieng } from '../../mock/dia-chi-rieng';
import { mockRequest, mockState } from '../../mock/transport';
import type { CustomHost, CustomHostList } from '../../modules/tenant/types';

const goc = (tenantId: string) => `/api/v1/tenants/${encodeURIComponent(tenantId)}/custom-hosts`;
const mot = (tenantId: string, hostId: string, duoi = '') =>
  `${goc(tenantId)}/${encodeURIComponent(hostId)}${duoi}`;

/** Máy chủ giả chạy **sau** nhịp chờ và các trạng thái `?mock=`: lỗi tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

const RONG: CustomHostList = { items: [], max_hosts: 3 };

/**
 * Domain thương hiệu của tenant (ADR-0097, tag `custom-hosts`), qua Gateway tới tenant-service. Đọc: Platform Admin
 * hoặc Tenant Admin của đúng tenant, cả đọc lẫn ghi. Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ
 * giả (`mock/dia-chi-rieng.ts`).
 */
export const diaChiRiengRepository = {
  /**
   * `GET …/custom-hosts` — không phân trang (tối đa 3 dòng). `?mock=filtered-empty` không áp dụng (màn không có bộ
   * lọc), nên trả như `empty`.
   */
  danhSach: (tenantId: string): Promise<CustomHostList> => {
    if (dungApiThat()) return goiApi<CustomHostList>(goc(tenantId));
    if (mockState() === 'filtered-empty') return mockRequest(RONG);
    return mockRequest(mayChuDiaChiRieng.danhSach(tenantId), RONG);
  },

  /** `POST …/custom-hosts` → `201`, có `Idempotency-Key` như mọi POST tạo tài nguyên. */
  them: (tenantId: string, hostname: string, khoaLap: string): Promise<CustomHost> =>
    dungApiThat()
      ? goiApi<CustomHost>(goc(tenantId), { method: 'POST', idempotencyKey: khoaLap, body: { hostname } })
      : giaLap(() => mayChuDiaChiRieng.them(tenantId, hostname)),

  /** `POST …/{host_id}/check` — DNS chưa đạt **không** phải lỗi: `200` kèm `dns_error`. */
  kiemDns: (tenantId: string, hostId: string): Promise<CustomHost> =>
    dungApiThat()
      ? goiApi<CustomHost>(mot(tenantId, hostId, '/check'), { method: 'POST' })
      : giaLap(() => mayChuDiaChiRieng.kiem(tenantId, hostId)),

  /** `POST …/{host_id}/activate` — luỹ đẳng; sai trạng thái là `400` trỏ `#/status`. */
  kichHoat: (tenantId: string, hostId: string): Promise<CustomHost> =>
    dungApiThat()
      ? goiApi<CustomHost>(mot(tenantId, hostId, '/activate'), { method: 'POST' })
      : giaLap(() => mayChuDiaChiRieng.kichHoat(tenantId, hostId)),

  /** `POST …/{host_id}/suspend` — luỹ đẳng. */
  tamNgung: (tenantId: string, hostId: string): Promise<CustomHost> =>
    dungApiThat()
      ? goiApi<CustomHost>(mot(tenantId, hostId, '/suspend'), { method: 'POST' })
      : giaLap(() => mayChuDiaChiRieng.tamNgung(tenantId, hostId)),

  /** `DELETE …/{host_id}` → `204`. */
  go: (tenantId: string, hostId: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(mot(tenantId, hostId), { method: 'DELETE' })
      : giaLap(() => mayChuDiaChiRieng.go(tenantId, hostId)),
};
